import { useQuery } from '@tanstack/react-query';
import CustomSelect from '../Shared/CustomSelect/CustomSelect';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { FormikErrors, FormikTouched } from 'formik';
import { t } from 'i18next';
import { IconButton, Textarea } from '~/ui';
import styles from './LeisureGeneral.module.scss';
import ImageSelect from '~/ui/ImageSelect/ImageSelect';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import { useContext, useEffect, useState } from 'react';
import { Modal } from '../Shared/Modal/Modal';
import PhotoPickerContainer from '../AnnouncementForm/PhotoPickerContainer/PhotoPickerContainer';
import { IFormikValues } from '~/pages/Leisure/Leisure';
import classNames from 'classnames';
import BtnIcon from '~/assets/svg/newSvg/picture.svg?react';
import { AuthenticationContext } from '../Authentication/AuthenticationProvider';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import cn from 'classnames';
import { IAccommodation } from '../UserForm/BusinessTripHistoryTable/BusinessTripTable/BusinessTripTable';

interface ILeisureGeneralProps {
  values: IFormikValues;
  setFieldValue: (name: string, value: any) => void;
  setFieldTouched: (name: string, value: boolean) => void;
  resetImage: () => void;
  errors: FormikErrors<IFormikValues>;
  touched: FormikTouched<IFormikValues>;
  accomodationsArr: IAccommodation[];
  selectedAccomodations: { id: number; name: string }[];
  selectedAssets: { id: number; name: string }[];
}

const LeisureGeneral = ({
  values,
  setFieldValue,
  setFieldTouched,
  resetImage,
  errors,
  touched,
  accomodationsArr,
  selectedAccomodations,
  selectedAssets,
}: ILeisureGeneralProps) => {
  const toastError = useToastError();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);

  const selectedAccomodationsId = selectedAccomodations.map(item => item.id);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFieldValue('uploadImage', file);
    }
  };

  const { data: assetsArray, isLoading: isAssetsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/assets');
      return data.data;
    },
    queryKey: ['assets'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: organizationsArr, isLoading: isOrganisationsLoading } =
    useQuery({
      queryFn: async () => {
        const { data } = await instance.get('admin/organization', {
          params: {
            sort: '',
            perPage: 100,
            field: '',
            page: 1,
            accommodation_ids: selectedAccomodationsId,
          },
        });
        return data;
      },
      queryKey: ['organizations', selectedAccomodationsId],
      onError: error => {
        toastError(error);
      },
    });

  useEffect(() => {
    if (!organizationsArr || isOrganisationsLoading) return;

    const newOrganisations = [...values.organizations].filter(
      item => organizationsArr?.some((el: { id: number }) => el.id === item.id)
    );

    if (!selectedAccomodations.length || !selectedAssets.length) {
      setFieldValue('organizations', []);
    } else {
      setFieldValue('organizations', newOrganisations);
    }
  }, [selectedAccomodations, organizationsArr, selectedAssets]);

  return (
    <div className={styles.container}>
      <div className={styles.selectWrapper}>
        {isSuperAdmin && (
          <CustomSelect
            placeholder={t('assets')}
            label={t('asset')}
            value={values.assets}
            options={assetsArray}
            disabled={isAssetsLoading}
            multiselect
            onChange={value => {
              setFieldValue('assets', value);
            }}
            error={errors.assets as string}
            onTouch={() => setFieldTouched('asset', true)}
            required
            className={cn(
              styles.select,
              isSuperAdmin && styles.superAdminSelect
            )}
          />
        )}
        <CustomSelect
          placeholder={t('select_accommodation')}
          label={t('accommodations')}
          value={values.assets.length === 0 ? null : values?.accommodations}
          options={accomodationsArr}
          disabled={!values.assets.length}
          onChange={value => setFieldValue('accommodations', value)}
          multiselect
          required
          error={errors.accommodations as string}
          className={cn(styles.select, isSuperAdmin && styles.superAdminSelect)}
        />
        <CustomSelect
          placeholder={t('select_least_one_organization')}
          label={t('organizations')}
          value={values.organizations}
          options={!values.accommodations.length ? [] : organizationsArr}
          disabled={
            isOrganisationsLoading ||
            !values.accommodations.length ||
            !values.assets.length
          }
          onChange={value => setFieldValue('organizations', value)}
          error={errors.organizations as string}
          multiselect
          required
          className={cn(styles.select, isSuperAdmin && styles.superAdminSelect)}
        />
      </div>
      <div>
        <Textarea
          label={t('description')}
          rows={4}
          star
          name="description"
          placeholder={t('description_game')}
          value={values.description}
          onChange={e => setFieldValue('description', e.target.value)}
          maxLength={1000}
          errors={errors.description}
          error={!touched.description && Boolean(errors.description)}
        />
      </div>
      {!true && (
        <div className={styles.imageBtnWrapper}>
          <ImageSelect
            image={values.image}
            setFieldValue={setFieldValue}
            setIsImageModalOpen={setIsModalOpen}
          />
          {errors.image && (
            <span className={styles.imageError}>
              {t('choose_photo_for_entertainment')}
            </span>
          )}
        </div>
      )}

      {values.uploadImage && (
        <div className={styles.uploadImageWrapper}>
          <img
            src={
              typeof values.uploadImage === 'string'
                ? values.uploadImage
                : URL.createObjectURL(values.uploadImage)
            }
            alt="Uploaded preview"
            className={styles.uploadImage}
          />
          <button
            className={styles.deleteImageBtn}
            type="button"
            onClick={() => setFieldValue('uploadImage', null)}
          >
            <CloseIcon className={styles.deleteImageIcon} />
          </button>
        </div>
      )}

      {!values.uploadImage && (
        <div className={styles.addImageBtnWrapper}>
          <input
            id="uploadImage"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className={styles.addImageBtnInput}
          />
          <IconButton
            onClick={() => document.getElementById('uploadImage')?.click()}
            className={classNames(styles.addImageBtn, styles.addImageBtnIcon)}
          >
            <BtnIcon /> {t('download_game_cover')}
          </IconButton>

          {errors.uploadImage && (
            <span className={styles.imageError}>
              {t('game_cover_validate')}
            </span>
          )}
        </div>
      )}

      <Modal
        modalStyles={styles.photosModal}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <PhotoPickerContainer
          initialPhotos={[]}
          selectedImages={values.image}
          onImageChange={value => setFieldValue('image', value)}
          onResetClick={resetImage}
          onCloseClick={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default LeisureGeneral;
